import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
// import { toast } from 'react-toastify';

import { noop } from '../lib/serviceHelpers';

export const fetchChatsByClaim = async (claimId) => {
  if (!claimId) return { chats: [] };
  const res = await axios.get(`/casegpt/chats/${claimId}`);
  return res.data;
};

export const fetchChatMessages = async (chatId) => {
  if (!chatId) return { messages: [] };

  const res = await axios.get(`/casegpt/chats/${chatId}/messages`);
  return res.data;
};

export const useDeleteChat = (callback, opts = {}) => {
  return useMutation(
    async (chatId) => {
      const res = await axios.delete(`/casegpt/chats/${chatId}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

/**
 * Fetch ingestion status for a claim's files
 * Checks Pinecone directly to see which files have been indexed
 */
export const useFetchIngestionStatus = (claimId, opts = {}) => {
  return useQuery(
    ['ingestion-status', claimId],
    async () => {
      const res = await axios.get(`/casegpt/claims/${claimId}/ingestion-status`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      enabled: !!claimId,
      retry: false, // Don't retry on failure
      ...opts,
    },
  );
};

/**
 * Trigger backfill for files not in Pinecone
 * Now returns a job ID for status tracking
 */
export const useTriggerBackfill = (callback, opts = {}) => {
  return useMutation(
    async ({ claimId, fileIds }) => {
      const res = await axios.post(`/casegpt/claims/${claimId}/backfill`, {
        fileIds,
      });
      return res.data;
    },
    {
      ...opts,
      onSuccess: callback || noop,
    },
  );
};

/**
 * Fetch job status by ID
 */
export const useFetchJobStatus = (jobId, opts = {}) => {
  return useQuery(
    ['job-status', jobId],
    async () => {
      const res = await axios.get(`/jobs/${jobId}`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!jobId,
      refetchInterval: (data) => {
        // Poll every 3 seconds while job is pending or processing
        if (data?.jobStatus === 'pending' || data?.jobStatus === 'processing') {
          return 3000;
        }
        return false; // Stop polling when completed/failed
      },
      ...opts,
    },
  );
};

/**
 * Fetch active file indexing job for a claim
 */
export const useFetchActiveFileIndexingJob = (claimId, opts = {}) => {
  return useQuery(
    ['active-file-indexing-job', claimId],
    async () => {
      const res = await axios.get(`/casegpt/claims/${claimId}/file-indexing-job`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!claimId,
      staleTime: 10000, // 10 seconds
      ...opts,
    },
  );
};
